/**
 * IdentityPanel — Narcis's `HouseTheme.identityPanel`. Nothing to choose here (the model is the
 * account's own), so `open`/`onOpenChange`/`onPreview` are never read and the frame always shows
 * the chain's picture, never the user's photo (operator, 2026-09-22). Three shapes off
 * `identity.state`: a dashed empty frame and a dim "no model yet"; the intake thumb with the canon's "creating your model" (no minutes)
 * and a track across the slot up to the verb (operator, 2026-09-22, over the board's half-bar);
 * the first take's thumb and the name. The track's fill is `identity.percent` when one stands
 * (`resolveMineIdentity`'s own read of the live job — wall review follow-ups unit 4, one poll
 * instead of `ModelStatusProvider`'s separate `/api/model-status` one) else an ambient shimmer.
 */
import type {IdentityPanelProps} from '@core/wall/ui/theme'
import {WALL_CREATING} from '@product/narcis/proposition'
import {takeImageUrl, trainingPhotoUrl} from './urls'
import styles from './IdentityPanel.module.scss'

export function IdentityPanel({identity}: IdentityPanelProps) {
    if (identity.kind !== 'model') return null

    if (identity.state === 'missing') {
        return (
            <span className={styles.chip}>
                <span className={`${styles.thumb} ${styles.thumbEmpty}`} />
                <span className={`${styles.name} ${styles.nameDim}`}>no model yet</span>
            </span>
        )
    }

    const thumbUrl = identity.thumb_key
        ? identity.state === 'training'
            ? trainingPhotoUrl(identity.thumb_key)
            : takeImageUrl(identity.thumb_key)
        : null

    const percent = identity.percent != null ? Math.max(0, Math.min(100, identity.percent)) : null

    return (
        <span className={styles.chip}>
            <span className={styles.thumb}>
                {thumbUrl ? <img src={thumbUrl} alt="" className={styles.thumbImg} /> : null}
            </span>
            {identity.state === 'training' ? (
                <span className={styles.prog}>
                    <span className={styles.line}>{WALL_CREATING}</span>
                    <span className={styles.track} aria-hidden="true">
                        {percent !== null
                            ? <span className={styles.fill} style={{width: `${percent}%`}} />
                            : <span className={styles.shimmer} />}
                    </span>
                </span>
            ) : (
                <span className={styles.name}>{identity.name}</span>
            )}
        </span>
    )
}
